import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { isChunkLoadError, reloadForFreshChunk } from '@/lib/chunk-error';

interface Props { children: ReactNode }
interface State { error: Error | null; info: ErrorInfo | null; reloading: boolean }

export default class RouteErrorBoundary extends Component<Props, State> {
  state: State = { error: null, info: null, reloading: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    return { error, reloading: isChunkLoadError(error) };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[RouteErrorBoundary]', error, info);
    // Chunk viejo tras deploy: no es un bug de la página, recargamos a la nueva.
    if (isChunkLoadError(error)) {
      // Si el guard anti-bucle frena la recarga (ya recargamos hace <12s y volvió
      // a fallar), no dejamos el spinner eterno: mostramos el fallback con escape.
      const reloaded = reloadForFreshChunk();
      if (!reloaded) this.setState({ reloading: false });
      return;
    }
    this.setState({ info });
  }

  reset = () => this.setState({ error: null, info: null, reloading: false });

  render() {
    if (this.state.reloading) {
      return (
        <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
          <div className="h-5 w-5 rounded-full border-2 border-emerald-400/40 border-t-emerald-400 animate-spin" />
          <p className="text-sm text-pelip-text-soft">Cargando la versión más reciente…</p>
        </div>
      );
    }
    if (!this.state.error) return this.props.children;
    return (
      <div className="max-w-2xl mx-auto mt-8 p-6 rounded-lg border border-red-500/30 bg-red-500/5">
        <div className="flex items-center gap-2 mb-3">
          <AlertTriangle className="text-red-600 dark:text-red-400" size={20} />
          <h2 className="text-lg font-semibold text-red-300">Error al renderizar esta página</h2>
        </div>
        <p className="text-sm text-pelip-text-soft mb-2 font-mono break-all">
          {this.state.error.message}
        </p>
        {this.state.error.stack && (
          <details className="mt-3">
            <summary className="text-xs text-pelip-text-faint cursor-pointer hover:text-pelip-text-soft">
              Stack trace
            </summary>
            <pre className="mt-2 text-[10px] text-pelip-text-faint whitespace-pre-wrap break-all">
              {this.state.error.stack}
            </pre>
          </details>
        )}
        <div className="flex gap-2 mt-4">
          <Button onClick={this.reset} size="sm" variant="outline" className="gap-1">
            <RefreshCw size={14} /> Reintentar
          </Button>
          <Button onClick={() => window.location.href = '/proyectos'} size="sm" variant="outline">
            Volver a Proyectos
          </Button>
        </div>
      </div>
    );
  }
}
